import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import logoBlue from "../../../../assets/logo.png";
import appleLogo from "../../../../assets/apple.png";
import playLogo from "../../../../assets/play.png";

const COLORS = {
  blue: "#2453FF",
  darkBlue: "#164CEB",
  navy: "#071D54",
  white: "#FFFFFF",
  black: "#111111",
  paleBlue: "#EEF3FF",
  softBlue: "#8DAEFF",
  lightBlue: "#DDE7FF",
  mutedBlue: "#6E91F7",
};

const WIDTH = 1086;
const HEIGHT = 1450;

/* =========================================================
   IMAGE LOADER
========================================================= */

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "anonymous";

    img.onload = () => resolve(img);

    img.onerror = (error) => {
      console.error("Failed to load image:", src);
      reject(error);
    };

    img.src = src;
  });
};

/* =========================================================
   HELPERS
========================================================= */

const roundedRect = (
  ctx,
  x,
  y,
  width,
  height,
  radius,
  fill,
  stroke = null,
  strokeWidth = 1
) => {
  const r = Math.min(radius, width / 2, height / 2);

  ctx.beginPath();

  ctx.moveTo(x + r, y);

  ctx.lineTo(x + width - r, y);

  ctx.quadraticCurveTo(
    x + width,
    y,
    x + width,
    y + r
  );

  ctx.lineTo(
    x + width,
    y + height - r
  );

  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - r,
    y + height
  );

  ctx.lineTo(x + r, y + height);

  ctx.quadraticCurveTo(
    x,
    y + height,
    x,
    y + height - r
  );

  ctx.lineTo(x, y + r);

  ctx.quadraticCurveTo(
    x,
    y,
    x + r,
    y
  );

  ctx.closePath();

  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }

  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    ctx.stroke();
  }
};

const drawText = (
  ctx,
  text,
  x,
  y,
  fontSize,
  fontWeight = "400",
  color = COLORS.navy,
  align = "left",
  fontFamily = "Arial, Helvetica, sans-serif"
) => {
  ctx.save();

  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = "alphabetic";

  ctx.fillText(String(text ?? ""), x, y);

  ctx.restore();
};

const drawCircle = (
  ctx,
  x,
  y,
  radius,
  fill,
  stroke = null,
  strokeWidth = 1
) => {
  ctx.save();

  ctx.beginPath();

  ctx.arc(
    x,
    y,
    radius,
    0,
    Math.PI * 2
  );

  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }

  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    ctx.stroke();
  }

  ctx.restore();
};

const drawLine = (
  ctx,
  x1,
  y1,
  x2,
  y2,
  color,
  width = 1,
  dash = []
) => {
  ctx.save();

  ctx.beginPath();

  if (dash.length) {
    ctx.setLineDash(dash);
  }

  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);

  ctx.strokeStyle = color;
  ctx.lineWidth = width;

  ctx.stroke();

  ctx.restore();
};

/* =========================================================
   LOGO
========================================================= */

const drawLogo = (
  ctx,
  logoImg,
  x,
  y,
  iconWidth,
  iconHeight,
  textSize
) => {
  if (logoImg) {
    ctx.drawImage(
      logoImg,
      x,
      y,
      iconWidth,
      iconHeight
    );
  }

  drawText(
    ctx,
    "Mogoma",
    x + iconWidth + 20,
    y + iconHeight * 0.73,
    textSize,
    "500",
    COLORS.navy,
    "left",
    "Georgia, Times New Roman, serif"
  );
};

/* =========================================================
   BACKGROUND DECORATION
========================================================= */

const drawBackgroundDecoration = (
  ctx,
  logoImg
) => {
  if (!logoImg) return;

  ctx.save();

  /*
    Large, very light Mogoma logo
    sitting BEHIND the Mogoma Trips badge.
  */

  ctx.globalAlpha = 0.065;

  ctx.drawImage(
    logoImg,
    665,
    -35,
    430,
    350
  );

  ctx.restore();
};

/* =========================================================
   TRIP CAR
========================================================= */

const drawTripCard = (
  ctx,
  {
    origin,
    destination,
    tripLabel,
    availableSpaces,
    totalCapacity,
  }
) => {
  const cardX = 70;
  const cardY = 570;
  const cardWidth = 946;
  const cardHeight = 570;

  roundedRect(
    ctx,
    cardX,
    cardY,
    cardWidth,
    cardHeight,
    46,
    COLORS.blue
  );

ctx.save();

ctx.shadowColor = "rgba(36, 83, 255, 0.22)";
ctx.shadowBlur = 28;
ctx.shadowOffsetX = 0;
ctx.shadowOffsetY = 0;

roundedRect(
  ctx,
  cardX,
  cardY,
  cardWidth,
  cardHeight,
  46,
  COLORS.blue
);

ctx.restore();

  /*
    TRIP PILL
  */

  roundedRect(
    ctx,
    cardX + 52,
    cardY + 45,
    390,
    48,
    24,
    "rgba(255,255,255,0.18)"
  );

  drawText(
    ctx,
    tripLabel,
    cardX + 72,
    cardY + 77,
    21,
    "500",
    COLORS.white
  );

  /*
    VERTICAL DIVIDER
  */

  const dividerX = cardX + 680;

  drawLine(
    ctx,
    dividerX,
    cardY + 45,
    dividerX,
    cardY + cardHeight - 45,
    "rgba(255,255,255,0.65)",
    3,
    [8, 8]
  );

  /*
    DIVIDER CIRCLES
  */

  drawCircle(
    ctx,
    dividerX,
    cardY,
    27,
    COLORS.white
  );

  drawCircle(
    ctx,
    dividerX,
    cardY + cardHeight,
    27,
    COLORS.white
  );

  /*
    ROUTE VERTICAL LINE
  */

  const routeX = cardX + 75;
  const routeTop = cardY + 145;
  const routeBottom = cardY + 340;

  drawLine(
    ctx,
    routeX,
    routeTop,
    routeX,
    routeBottom,
    COLORS.white,
    4,
    [7, 7]
  );

  /*
    ORIGIN DOT
  */

  drawCircle(
    ctx,
    routeX,
    routeTop,
    10,
    COLORS.white
  );

  /*
    CAR ICON CIRCLE
  */

  drawCircle(
    ctx,
    routeX,
    cardY + 245,
    20,
    COLORS.white
  );

  /*
    SIMPLE CAR ICON
  */

  ctx.save();

  ctx.strokeStyle = COLORS.blue;
  ctx.lineWidth = 3;
  ctx.lineCap = "round";

  roundedRect(
    ctx,
    routeX - 9,
    cardY + 238,
    18,
    11,
    3,
    null,
    COLORS.blue,
    2
  );

  drawCircle(
    ctx,
    routeX - 6,
    cardY + 251,
    2.5,
    COLORS.blue
  );

  drawCircle(
    ctx,
    routeX + 6,
    cardY + 251,
    2.5,
    COLORS.blue
  );

  ctx.restore();

  /*
    DESTINATION DIAMOND
  */

  ctx.save();

  ctx.translate(
    routeX,
    routeBottom
  );

  ctx.rotate(Math.PI / 4);

  roundedRect(
    ctx,
    -8,
    -8,
    16,
    16,
    3,
    COLORS.white
  );

  ctx.restore();

  /*
    FROM
  */

  drawText(
    ctx,
    "From",
    routeX + 47,
    cardY + 151,
    20,
    "500",
    "rgba(255,255,255,0.85)"
  );

  /*
    ORIGIN
  */

  drawText(
    ctx,
    origin,
    routeX + 47,
    cardY + 215,
    54,
    "800",
    COLORS.white
  );

  /*
    TO
  */

  drawText(
    ctx,
    "To",
    routeX + 47,
    cardY + 285,
    20,
    "500",
    "rgba(255,255,255,0.85)"
  );

  /*
    DESTINATION
  */

  drawText(
    ctx,
    destination,
    routeX + 47,
    cardY + 350,
    54,
    "800",
    COLORS.white
  );

  /*
    PACKAGE SPACES
  */

  const packageY = cardY + 405;

  const packageSize = 67;
  const packageGap = 11;

  const packageStartX = cardX + 52;

  const occupiedSpaces = Math.max(
    0,
    totalCapacity - availableSpaces
  );

  for (
    let i = 0;
    i < totalCapacity;
    i++
  ) {
    const x =
      packageStartX +
      i * (packageSize + packageGap);

    if (x + packageSize > dividerX - 35) {
      break;
    }

    if (i < occupiedSpaces) {
      roundedRect(
        ctx,
        x,
        packageY,
        packageSize,
        packageSize,
        18,
        COLORS.white
      );

      drawPackageIcon(
        ctx,
        x + packageSize / 2,
        packageY + packageSize / 2
      );
    } else {
    ctx.save();

ctx.setLineDash([4, 5]);
ctx.strokeStyle = "rgba(255,255,255,0.85)";
ctx.lineWidth = 2;

roundedRect(
  ctx,
  x,
  packageY,
  packageSize,
  packageSize,
  18,
  null,
  "rgba(255,255,255,0.85)",
  2
);

ctx.restore();

      drawText(
        ctx,
        "+",
        x + packageSize / 2,
        packageY + 47,
        34,
        "600",
        COLORS.white,
        "center"
      );
    }
  }

  /*
    RIGHT SIDE
  */

  drawText(
    ctx,
    String(availableSpaces),
    dividerX + 130,
    cardY + 280,
    120,
    "900",
    COLORS.white,
    "center"
  );

  drawText(
    ctx,
    availableSpaces === 1
      ? "space left"
      : "spaces left",
    dividerX + 130,
    cardY + 345,
    30,
    "500",
    COLORS.white,
    "center"
  );

  /*
    BOOK BUTTON
  */

  roundedRect(
    ctx,
    dividerX + 45,
    cardY + 405,
    205,
    60,
    30,
    COLORS.white
  );

  drawText(
    ctx,
    "Book a space",
    dividerX + 147,
    cardY + 443,
    20,
    "800",
    COLORS.blue,
    "center"
  );
};

/* =========================================================
   PACKAGE ICON
========================================================= */

const drawPackageIcon = (
  ctx,
  centerX,
  centerY
) => {
  ctx.save();

  ctx.strokeStyle = COLORS.blue;
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";

  ctx.beginPath();

  ctx.moveTo(
    centerX - 11,
    centerY - 7
  );

  ctx.lineTo(
    centerX,
    centerY - 13
  );

  ctx.lineTo(
    centerX + 11,
    centerY - 7
  );

  ctx.lineTo(
    centerX,
    centerY
  );

  ctx.closePath();

  ctx.stroke();

  ctx.beginPath();

  ctx.moveTo(
    centerX - 11,
    centerY - 7
  );

  ctx.lineTo(
    centerX - 11,
    centerY + 8
  );

  ctx.lineTo(
    centerX,
    centerY + 14
  );

  ctx.lineTo(
    centerX,
    centerY
  );

  ctx.stroke();

  ctx.beginPath();

  ctx.moveTo(
    centerX + 11,
    centerY - 7
  );

  ctx.lineTo(
    centerX + 11,
    centerY + 8
  );

  ctx.lineTo(
    centerX,
    centerY + 14
  );

  ctx.stroke();

  ctx.restore();
};

/* =========================================================
   APP STORE ICONS
========================================================= */


/* =========================================================
   APP BUTTON
========================================================= */

const drawAppButton = (
  ctx,
  x,
  y,
  width,
  height,
  type,
  appleImg,
  playImg
) => {
  roundedRect(
    ctx,
    x,
    y,
    width,
    height,
    32,
    COLORS.blue
  );

  const icon =
    type === "apple"
      ? appleImg
      : playImg;

  if (icon) {
    const iconWidth = 52;
const iconHeight = 43;

ctx.drawImage(
  icon,
  x + 20,
  y + (height - iconHeight) / 2,
  iconWidth,
  iconHeight
);
  }

  if (type === "apple") {
    drawText(
      ctx,
      "Download on the",
      x + 78,
      y + 29,
      15,
      "400",
      COLORS.white
    );

    drawText(
      ctx,
      "App Store",
      x + 78,
      y + 55,
      28,
      "500",
      COLORS.white
    );
  } else {
    drawText(
      ctx,
      "GET IT ON",
      x + 78,
      y + 25,
      13,
      "500",
      COLORS.white
    );

    drawText(
      ctx,
      "Google Play",
      x + 78,
      y + 53,
      25,
      "500",
      COLORS.white
    );
  }
};
/* =========================================================
   COMPONENT
========================================================= */

const GenerateTripPoster = forwardRef(
  ({ trip }, ref) => {
    const canvasRef = useRef(null);

    const safeTrip = trip || {};

    /*
      LOCATION
    */

    const getLocationName = (value, fallback) => {
      if (!value) return fallback;

      return String(value)
        .split(",")[0]
        .trim();
    };

    const origin = getLocationName(
      safeTrip.origin,
      "Gaborone"
    );

    const destination = getLocationName(
      safeTrip.destination,
      "Francistown"
    );

    /*
      DATE
    */

    const formatDate = (value) => {
      if (!value) return "Date TBC";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "Date TBC";
      }

      return date.toLocaleDateString(
        "en-GB",
        {
          weekday: "short",
          day: "2-digit",
          month: "short",
        }
      );
    };

    /*
      TIME
    */

    const formatTime = (value) => {
      if (!value) return "TBC";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "TBC";
      }

      return date.toLocaleTimeString(
        "en-GB",
        {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }
      );
    };

    const departureDate = useMemo(
      () =>
        formatDate(
          safeTrip.departure_time
        ),
      [safeTrip.departure_time]
    );

    const departureTime = useMemo(
      () =>
        formatTime(
          safeTrip.departure_time
        ),
      [safeTrip.departure_time]
    );

    /*
      CAPACITY
    */

    const totalCapacity = Math.max(
      1,
      Number(
        safeTrip.capacity_packages ??
          safeTrip.package_capacity ??
          safeTrip.capacity ??
          6
      )
    );

    /*
      AVAILABLE SPACES

      Supports different possible API field names.
    */

    const availableSpaces = Math.max(
      0,
      Math.min(
        totalCapacity,
        Number(
          safeTrip.available_spaces ??
            safeTrip.spaces_left ??
            safeTrip.remaining_capacity ??
            totalCapacity
        )
      )
    );

    /*
      TRIP LABEL
    */

    const tripLabel = `Mogoma Trip · ${departureDate} ${departureTime}`;

    /*
      GENERATE
    */

    const generatePoster = async () => {
      const canvas = canvasRef.current;

      if (!canvas) {
        throw new Error(
          "Poster canvas not found"
        );
      }

      const ctx =
        canvas.getContext("2d");

      if (!ctx) {
        throw new Error(
          "Canvas context unavailable"
        );
      }

      canvas.width = WIDTH;
      canvas.height = HEIGHT;

      /*
        LOAD LOGO
      */

      const logoImg =
        await loadImage(logoBlue);
      const appleImg =
        await loadImage(appleLogo);
      const playImg =
        await loadImage(playLogo);

      /*
        BACKGROUND
      */

      ctx.clearRect(
        0,
        0,
        WIDTH,
        HEIGHT
      );

      ctx.fillStyle =
        COLORS.white;

      ctx.fillRect(
        0,
        0,
        WIDTH,
        HEIGHT
      );

      /*
        DECORATION
      */

      drawBackgroundDecoration(ctx);

      /*
        MAIN LOGO
      */

      drawLogo(
        ctx,
        logoImg,
        68,
        69,
        72,
        56,
        54
      );

      /*
        MOGOMA TRIPS PILL
      */

        drawBackgroundDecoration(
  ctx,
  logoImg
);


      roundedRect(
        ctx,
        793,
        76,
        215,
        54,
        28,
        COLORS.white,
        COLORS.blue,
        2
      );

      drawText(
        ctx,
        "Mogoma Trips",
        900,
        111,
        23,
        "500",
        COLORS.blue,
        "center"
      );

      /*
        HEADLINE
      */

      drawText(
        ctx,
        "book a",
        68,
        305,
        100,
        "900",
        COLORS.blue
      );

      drawText(
        ctx,
        "space",
        68,
        442,
        100,
        "900",
        COLORS.blue
      );

      /*
        PERIOD
      */

      drawCircle(
        ctx,
        496,
        433,
        15,
        COLORS.softBlue
      );

      /*
        SUBTITLE
      */

      drawText(
        ctx,
        "On our cars. On pre-posted routes.",
        70,
        525,
        31,
        "500",
        COLORS.blue
      );

      /*
        TRIP CARD
      */

      drawTripCard(ctx, {
        origin,
        destination,
        tripLabel,
        availableSpaces,
        totalCapacity,
      });

      /*
        TAGLINE
      */

 drawText(
  ctx,
  "Mo tshwarele gone hoo!",
  WIDTH / 2,
  1302,
  43,
  "italic 700",
  COLORS.blue,
  "center",
  "Georgia, Times New Roman, serif"
);

      /*
        APP STORE BUTTONS

        NO QR CODES.
      */

      const buttonY = 1350;

      drawAppButton(
  ctx,
  268,
  buttonY,
  255,
  76,
  "apple",
  appleImg,
  playImg
);

drawAppButton(
  ctx,
  557,
  buttonY,
  255,
  76,
  "google",
  appleImg,
  playImg
);

      return canvas;
    };

    /*
      DOWNLOAD
    */

    const downloadPoster = async () => {
      try {
        const canvas =
          await generatePoster();

        const safeOrigin =
          origin.replace(
            /[^a-zA-Z0-9-_]/g,
            "-"
          );

        const safeDestination =
          destination.replace(
            /[^a-zA-Z0-9-_]/g,
            "-"
          );

        const safeDate =
          departureDate.replace(
            /[^a-zA-Z0-9-_]/g,
            "-"
          );

        const filename =
          `mogoma-trip-${safeOrigin}-${safeDestination}-${safeDate}.png`;

        const dataUrl =
          canvas.toDataURL(
            "image/png",
            1
          );

        const link =
          document.createElement("a");

        link.href = dataUrl;
        link.download = filename;

        document.body.appendChild(link);

        link.click();

        link.remove();

        return true;
      } catch (error) {
        console.error(
          "Failed to download Mogoma trip poster:",
          error
        );

        throw error;
      }
    };

    /*
      IMPERATIVE REF
    */

    useImperativeHandle(
      ref,
      () => ({
        download: downloadPoster,
        generatePoster: downloadPoster,
      }),
      [
        origin,
        destination,
        departureDate,
        departureTime,
        availableSpaces,
        totalCapacity,
      ]
    );

    /*
      OFF-SCREEN CANVAS
    */

    return (
      <canvas
        ref={canvasRef}
        width={WIDTH}
        height={HEIGHT}
        style={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: `${WIDTH}px`,
          height: `${HEIGHT}px`,
          pointerEvents: "none",
        }}
      />
    );
  }
);

GenerateTripPoster.displayName =
  "GenerateTripPoster";

export default GenerateTripPoster;